import PersonSearchOutlinedIcon from "@mui/icons-material/PersonSearchOutlined";
import { alpha, Box, Typography, useTheme } from "@mui/material";
import { type FC, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useAnalytics } from "../../../../contexts/analytics/hooks";
import { useHospitalData } from "../../../../contexts/HospitalDataProvider";
import { ALPHA, FONT_SIZE, INK, NEUTRAL } from "../../../../theme/tokens";
import MetaChip from "../../../UIToolkit/Badges/MetaChip";
import { SystemLinearMeter } from "../../../UIToolkit/DataViz/SystemLinearMeter/SystemLinearMeter";
import { AccentIconTile } from "../../../UIToolkit/molecules/AccentIconTile";
import { EyebrowLabel } from "../../../UIToolkit/molecules/EyebrowLabel";
import { FocalCard } from "../../../UIToolkit/molecules/FocalCard";
import { SheetStep } from "../../../UIToolkit/Popups/SheetStep";
import { SheetStepButton } from "../../../UIToolkit/Popups/SheetStepButton";
import { getStaffingStatusStyle } from "../../WeeklyGapView/CountBadge";
import { formatStaffCount, type GapsShiftGroup, isShownAsShort } from "../gapsDayModel";
import { coverPromptFor } from "../gapsGroupSheets";
import type { GapsRemovalOutcome } from "./gapsSheetTypes";

type Props = {
	outcome: GapsRemovalOutcome;
	/** The group as the rebuilt day now has it, or `undefined` when the removal took its window off the day. */
	group: GapsShiftGroup | undefined;
	/** Opens the post sheet prefilled from this group. */
	onPostShift: (groupKey: string) => void;
	onClose: () => void;
};

const SHEET = "gaps-cover-prompt";

/**
 * What the removal left behind, and the one thing to do about it (P3-4).
 *
 * The figure is the group's own `filled/required` as the home now prints it, drawn big — it is the question the
 * whole screen exists to answer, asked again at the moment it just changed. Under it, the group's meter with the
 * removed person's share left as a faint ghost past the fill, so "what did that cost" reads without arithmetic.
 * Short and posted sit side by side and are NOT netted (`coverPromptFor`): a posted request is an ask, not a person.
 *
 * When the call-off agent took the removal, the backend is already looking for someone, so the prompt says that
 * instead of offering a second, competing post.
 */
export const GapsCoverPromptStep: FC<Props> = ({ outcome, group, onPostShift, onClose }) => {
	const { t } = useTranslation();
	const theme = useTheme();
	const { track } = useAnalytics();
	const { chosenDepartment } = useHospitalData();
	const cover = group === undefined ? undefined : coverPromptFor(group);
	const isShort = cover !== undefined && isShownAsShort(cover.short);
	// "Removed · <reason>" only with a reason: a self-scheduling or hospital removal records none of its own here.
	const reasonLine = outcome.reason === "" ? undefined : t("gaps.callIn.cover.subtitle", { reason: outcome.reason });

	// Once per prompt: the numbers can re-settle while the day rebuilds, but the prompt was shown once.
	// biome-ignore lint/correctness/useExhaustiveDependencies: deliberately fires on mount only.
	useEffect(() => {
		track("gaps_cover_prompt_shown", {
			department_id: chosenDepartment.department_id,
			group: outcome.groupKey,
			call_off_agent: outcome.callOffFired,
			...(cover === undefined ? {} : { short: cover.short, posted: cover.posted }),
		});
	}, []);

	if (outcome.callOffFired) {
		return (
			<SheetStep
				stepKey="cover-agent"
				dataTestId={SHEET}
				leading={
					<AccentIconTile size="sm" component="span">
						<PersonSearchOutlinedIcon />
					</AccentIconTile>
				}
				title={t("gaps.callIn.cover.findingTitle", { name: outcome.name })}
				subtitle={t("gaps.callIn.cover.findingBody")}
				footer={
					<SheetStepButton tone="confirm" onClick={onClose} dataTestId={`${SHEET}-confirm`}>
						{t("done")}
					</SheetStepButton>
				}
			>
				<Box data-testid={`${SHEET}-agent`} />
			</SheetStep>
		);
	}

	if (group === undefined || cover === undefined) {
		return (
			<SheetStep
				stepKey="cover-gone"
				dataTestId={SHEET}
				title={t("gaps.callIn.cover.removedTitle", { name: outcome.name })}
				subtitle={reasonLine}
				footer={
					<SheetStepButton tone="confirm" onClick={onClose} dataTestId={`${SHEET}-confirm`}>
						{t("done")}
					</SheetStepButton>
				}
			>
				<Box data-testid={`${SHEET}-no-group`} />
			</SheetStep>
		);
	}

	const staffing = getStaffingStatusStyle(group.actual, group.required);
	// Red whenever the window is short on its printed value — this is the prompt that asks you to cover it — and the
	// desktop's own amber when over; the meter's ghost is the same ink, faint.
	const ink = isShort ? theme.palette.error.main : staffing.isFlagged ? staffing.color : theme.palette.success.main;

	return (
		<SheetStep
			stepKey="cover"
			dataTestId={SHEET}
			title={t("gaps.callIn.cover.title", { name: outcome.name, job: group.jobLabel, time: group.timeLabel })}
			subtitle={reasonLine}
			footer={
				isShort ? (
					<>
						<SheetStepButton tone="dismiss" onClick={onClose} dataTestId={`${SHEET}-cancel`}>
							{t("gaps.callIn.cover.notNow")}
						</SheetStepButton>
						<SheetStepButton tone="confirm" onClick={() => onPostShift(outcome.groupKey)} dataTestId={`${SHEET}-confirm`}>
							{t("gaps.group.postShift")}
						</SheetStepButton>
					</>
				) : (
					<SheetStepButton tone="confirm" onClick={onClose} dataTestId={`${SHEET}-confirm`}>
						{t("done")}
					</SheetStepButton>
				)
			}
		>
			{/* The window's own ink on its focal card, so the card reads as THIS window's state, not a neutral panel. */}
			<FocalCard ink={ink} dataTestId={`${SHEET}-hero`} sx={{ mx: 2, my: 2, px: 2, pt: 1.75, pb: 2 }}>
				<EyebrowLabel>{t("gaps.callIn.cover.filledLabel", { job: group.jobLabel, time: group.timeLabel })}</EyebrowLabel>
				<Box sx={{ display: "flex", alignItems: "baseline", gap: 0.5, mt: 0.5, fontVariantNumeric: "tabular-nums" }}>
					<Typography data-testid={`${SHEET}-filled`} component="span" sx={{ fontSize: FONT_SIZE.STAT_VALUE, fontWeight: 800, lineHeight: 1.1, color: ink }}>
						{formatStaffCount(group.actual)}
					</Typography>
					<Typography component="span" sx={{ fontSize: FONT_SIZE.TITLE_MD, fontWeight: 600, color: INK.MUTED }}>
						{`/${formatStaffCount(group.required)}`}
					</Typography>
				</Box>
				<SystemLinearMeter
					value={group.actual}
					max={group.required}
					color={ink}
					// The removed share, left as a ghost past the fill: before the removal the window reached this far.
					secondaryValue={outcome.before?.actual}
					secondaryFill={alpha(ink, ALPHA.METER_EMPTY_BAR)}
					trackColor={NEUTRAL[150]}
					ariaLabel={t("gaps.callIn.cover.meterLabel", { filled: formatStaffCount(group.actual), required: formatStaffCount(group.required) })}
					dataTestId={`${SHEET}-meter`}
					sx={{ mt: 1.25 }}
				/>
				<Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, mt: 1.5 }}>
					<MetaChip
						label={isShort ? t("gaps.callIn.cover.short", { count: Number(formatStaffCount(cover.short)) }) : t("gaps.callIn.cover.covered")}
						tone={isShort ? "error" : "success"}
						dataTestId={`${SHEET}-short`}
					/>
					<MetaChip label={t("gaps.group.posted", { count: cover.posted })} tone={cover.posted > 0 ? "success" : "neutral"} dataTestId={`${SHEET}-posted`} />
				</Box>
			</FocalCard>
		</SheetStep>
	);
};
